import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { LeadQueueService } from 'src/app/lead-queue/lead-queue.service';
import {
    assignRecruiterToGroup,
    assignRecruiterToGroupFailure,
    assignRecruiterToGroupSuccessfully,
    assignRecruiterToLocation,
    assignRecruiterToLocationFailure,
    assignRecruiterToLocationSuccessfully,
    createProfessionGroup,
    createProfessionGroupFailure,
    createProfessionGroupSuccessfully,
    deleteProfessionGroup,
    deleteProfessionGroupFailure,
    deleteProfessionGroupProfession,
    deleteProfessionGroupProfessionFailure,
    deleteProfessionGroupProfessionSpecialty,
    deleteProfessionGroupProfessionSpecialtyFailure,
    deleteProfessionGroupProfessionSpecialtySuccessfully,
    deleteProfessionGroupProfessionSuccessfully,
    deleteProfessionGroupSuccessfully,
    loadExpertisesByProfIds,
    loadExpertisesByProfIdsFailure,
    loadExpertisesByProfIdsSuccessfully,
    loadProfessionGroupAssignedRecruiters,
    loadProfessionGroupAssignedRecruitersFailure,
    loadProfessionGroupAssignedRecruitersSuccessfully,
    loadProfessionGroupIdsWithPausedRecruiters,
    loadProfessionGroupIdsWithPausedRecruitersFailure,
    loadProfessionGroupIdsWithPausedRecruitersSuccessfully,
    loadRecruiterGrouping,
    loadRecruiterGroupingFailure,
    loadRecruiterGroupingSuccessfully,
    setRecruiterActive,
    setRecruiterActiveFailure,
    setRecruiterActiveSuccessfully,
    setRecruiterLeadQueueToggle,
    setRecruiterLeadQueueToggleSuccessfully,
    setRecruiterLeadQueueToggleFailure,
    unassignRecruiterFromGroup,
    unassignRecruiterFromLocation,
    unassignRecruiterFromLocationFailure,
    unassignRecruiterFromLocationSuccessfully,
    updateProfessionGroup,
    updateProfessionGroupFailure,
    updateProfessionGroupPauseStatuses,
    updateProfessionGroupPauseStatusesFailure,
    updateProfessionGroupPauseStatusesSuccessfully,
    updateProfessionGroupProfession,
    updateProfessionGroupProfessionFailure,
    updateProfessionGroupProfessionSpecialty,
    updateProfessionGroupProfessionSpecialtyFailure,
    updateProfessionGroupProfessionSpecialtySuccessfully,
    updateProfessionGroupProfessionSuccessfully,
    updateProfessionGroupSuccessfully,
    setRecruiterLineOfBusiness,
    setRecruiterLineOfBusinessSuccessfully,
    setRecruiterLineOfBusinessFailure,
    unassignRecruiterFromGroupSuccessfully,
    unassignRecruiterFromGroupFailure
} from 'src/app/lead-queue/store/actions/lead-queue.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ProfessionsAndExpertiseService } from 'src/app/shared/services/professions-and-expertise.service';
import { LeadQueueState } from '../state/lead-queue.state';

@Injectable()
export class LeadQueueEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store<LeadQueueState>,
        private readonly _leadQueueService: LeadQueueService,
        private readonly _profAndExpertiseService: ProfessionsAndExpertiseService
    ) {}

    getRecruiterGroupings$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                loadRecruiterGrouping,
                deleteProfessionGroupSuccessfully,
                createProfessionGroupSuccessfully,
                updateProfessionGroupSuccessfully,
                assignRecruiterToLocationSuccessfully,
                unassignRecruiterFromLocationSuccessfully,
                assignRecruiterToGroupSuccessfully,
                unassignRecruiterFromGroupSuccessfully
            ),
            switchMap(() => {
                return this._leadQueueService.getRecruiterGrouping().pipe(
                    map((result) => loadRecruiterGroupingSuccessfully({ grouping: result })),
                    catchError((error) => of(loadRecruiterGroupingFailure({ error })))
                );
            })
        )
    );

    getProfessionGroupAssignedRecruiters$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadProfessionGroupAssignedRecruiters),
            switchMap((action) => {
                return this._leadQueueService.getRecruitersAssignedToProfessionGroup(action.professionGroupId).pipe(
                    map((result) =>
                        loadProfessionGroupAssignedRecruitersSuccessfully({ professionGroupRecruiters: result })
                    ),
                    catchError((error) => of(loadProfessionGroupAssignedRecruitersFailure({ error })))
                );
            })
        )
    );

    getProfessionGroupIdsWithPausedRecruiters$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadProfessionGroupIdsWithPausedRecruiters),
            switchMap((action) => {
                return this._leadQueueService.getProfessionGroupIdsWithPausedRecruiters().pipe(
                    map((result) =>
                        loadProfessionGroupIdsWithPausedRecruitersSuccessfully({
                            professionGroupIdsWithPausedRecruiters: result
                        })
                    ),
                    catchError((error) => of(loadProfessionGroupIdsWithPausedRecruitersFailure({ error })))
                );
            })
        )
    );

    updateProfessionGroupPauseStatuses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateProfessionGroupPauseStatuses),
            switchMap((action) => {
                return this._leadQueueService
                    .updateRecruiterProfessionGroupPauseStatuses(
                        action.professionGroupId,
                        action.recruiterPauseStatuses
                    )
                    .pipe(
                        map((result) => {
                            this._store.dispatch(
                                loadProfessionGroupAssignedRecruiters({ professionGroupId: action.professionGroupId })
                            );
                            return updateProfessionGroupPauseStatusesSuccessfully({ result: result });
                        }),
                        catchError((error) => of(updateProfessionGroupPauseStatusesFailure({ error })))
                    );
            })
        )
    );

    deleteProfessionGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteProfessionGroup),
            switchMap((action) => {
                return this._leadQueueService.deleteProfessionGroup(action.groupId).pipe(
                    map(() => deleteProfessionGroupSuccessfully()),
                    catchError((error) => of(deleteProfessionGroupFailure({ error })))
                );
            })
        )
    );

    createProfessionGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createProfessionGroup),
            switchMap((action) => {
                return this._leadQueueService.createProfessionGroup(action.name, action.parentGroupId).pipe(
                    map((result) => createProfessionGroupSuccessfully({ result: result })),
                    catchError(() =>
                        of(
                            createProfessionGroupFailure({
                                error: 'Failed to create profession group. Names must be different'
                            })
                        )
                    )
                );
            })
        )
    );

    updateProfessionGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateProfessionGroup),
            switchMap((action) => {
                return this._leadQueueService.updateProfessionGroupName(action.name, action.professionGroupId).pipe(
                    map((result) => updateProfessionGroupSuccessfully({ result })),
                    catchError(() =>
                        of(
                            updateProfessionGroupFailure({
                                error: 'Failed to update profession group. Names must be different'
                            })
                        )
                    )
                );
            })
        )
    );

    getExpertisesByProfIds$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadExpertisesByProfIds),
            switchMap((action) => {
                return this._profAndExpertiseService.getExpertiseForProfessions(action.professionIds).pipe(
                    map((expertises) => loadExpertisesByProfIdsSuccessfully({ expertises })),
                    catchError((error) => of(loadExpertisesByProfIdsFailure({ error })))
                );
            })
        )
    );

    updateProfessionGroupProfession$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateProfessionGroupProfession),
            switchMap((action) => {
                return this._leadQueueService
                    .updateProfessionGroupProfession(action.professionGroupId, action.professionId)
                    .pipe(
                        map(() => updateProfessionGroupProfessionSuccessfully()),
                        catchError((error) => of(updateProfessionGroupProfessionFailure({ error })))
                    );
            })
        )
    );

    deleteProfessionGroupProfession$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteProfessionGroupProfession),
            switchMap((action) => {
                return this._leadQueueService
                    .deleteProfessionGroupProfession(action.professionGroupId, action.professionId)
                    .pipe(
                        map(() => deleteProfessionGroupProfessionSuccessfully()),
                        catchError((error) => of(deleteProfessionGroupProfessionFailure({ error })))
                    );
            })
        )
    );

    updateProfessionGroupProfessionSpecialty$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateProfessionGroupProfessionSpecialty),
            switchMap((action) => {
                return this._leadQueueService
                    .updateProfessionGroupProfessionSpecialty(
                        action.professionGroupId,
                        action.professionId,
                        action.expertiseId
                    )
                    .pipe(
                        map(() => updateProfessionGroupProfessionSpecialtySuccessfully()),
                        catchError((error) => of(updateProfessionGroupProfessionSpecialtyFailure({ error })))
                    );
            })
        )
    );

    deleteProfessionGroupProfessionSpecialty$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteProfessionGroupProfessionSpecialty),
            switchMap((action) => {
                return this._leadQueueService
                    .deleteProfessionGroupProfessionSpecialty(
                        action.professionGroupId,
                        action.professionId,
                        action.expertiseId
                    )
                    .pipe(
                        map(() => deleteProfessionGroupProfessionSpecialtySuccessfully()),
                        catchError((error) => of(deleteProfessionGroupProfessionSpecialtyFailure({ error })))
                    );
            })
        )
    );

    assignRecruiterToGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(assignRecruiterToGroup),
            concatMap((action) => {
                return this._leadQueueService.assignRecruiterToGroup(action.recruiterId, action.professionGroupId).pipe(
                    map(() => {
                        return assignRecruiterToGroupSuccessfully();
                    }),
                    catchError((error) => of(assignRecruiterToGroupFailure({ error })))
                );
            })
        )
    );

    unassignRecruiterFromGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignRecruiterFromGroup),
            concatMap((action) => {
                return this._leadQueueService
                    .unassignRecruiterFromGroup(action.recruiterId, action.professionGroupId)
                    .pipe(
                        map(() => {
                            return unassignRecruiterFromGroupSuccessfully();
                        }),
                        catchError((error) => of(unassignRecruiterFromGroupFailure({ error })))
                    );
            })
        )
    );

    assignRecruiterToLocation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(assignRecruiterToLocation),
            switchMap((action) => {
                return this._leadQueueService.assignRecruiterToLocation(action.recruiterId, action.locationId).pipe(
                    map(() => assignRecruiterToLocationSuccessfully()),
                    catchError((error) => of(assignRecruiterToLocationFailure({ error })))
                );
            })
        )
    );

    unassignRecruiterFromLocation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignRecruiterFromLocation),
            switchMap((action) => {
                return this._leadQueueService.unassignRecruiterFromLocation(action.recruiterId, action.locationId).pipe(
                    map(() => unassignRecruiterFromLocationSuccessfully()),
                    catchError((error) => of(unassignRecruiterFromLocationFailure({ error })))
                );
            })
        )
    );

    setRecruiterActive$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterActive),
            switchMap((action) => {
                return this._leadQueueService.setRecruiterActive(action.recruiterId, action.status).pipe(
                    map(() => setRecruiterActiveSuccessfully()),
                    catchError((error) => of(setRecruiterActiveFailure({ error })))
                );
            })
        )
    );

    setRecruiterLeadQueueToggle$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterLeadQueueToggle),
            switchMap((action) => {
                return this._leadQueueService
                    .setRecruiterLeadQueueToggle(action.id, action.leadQueueToggleAvailable)
                    .pipe(
                        map(() => setRecruiterLeadQueueToggleSuccessfully()),
                        catchError((error) => of(setRecruiterLeadQueueToggleFailure({ error })))
                    );
            })
        )
    );

    setRecruiterLineOfBusiness$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterLineOfBusiness),
            switchMap((action) => {
                if (action.isTrue) {
                    return this._leadQueueService
                        .addRecruiterToLineOfBusiness(action.recruiterLeadInfoId, action.lineOfBusinessId)
                        .pipe(
                            map(() => setRecruiterLineOfBusinessSuccessfully()),
                            catchError((error) => of(setRecruiterLineOfBusinessFailure({ error })))
                        );
                } else {
                    return this._leadQueueService
                        .removeRecruiterFromLineOfBusiness(action.recruiterLeadInfoId, action.lineOfBusinessId)
                        .pipe(
                            map(() => setRecruiterLineOfBusinessSuccessfully()),
                            catchError((error) => of(setRecruiterLineOfBusinessFailure({ error })))
                        );
                }
            })
        )
    );

    showToasterCreateMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createProfessionGroupSuccessfully),
            map(() => showToasterMessage({ message: 'Created profession group successfully' }))
        )
    );

    showToasterUpdateMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateProfessionGroupSuccessfully),
            map(() => showToasterMessage({ message: 'Updated profession group successfully' }))
        )
    );

    showToasterStatusUpdateMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterActiveSuccessfully),
            map(() => showToasterMessage({ message: 'Successfully update recruiter active status.' }))
        )
    );

    showToasterStatusUpdateFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterActiveFailure),
            map(() => showToasterMessage({ message: 'Failed to update recruiter active status.' }))
        )
    );

    showToasterLeadQueueToggleUpdateSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterLeadQueueToggleSuccessfully),
            map(() => showToasterMessage({ message: 'Successfully update recruiter lead queue toggle.' }))
        )
    );

    showToasterLeadQueueToggleUpdateFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterLeadQueueToggleFailure),
            map(() => showToasterMessage({ message: 'Failed to update recruiter lead queue toggle.' }))
        )
    );

    showToasterPauseStatusUpdateMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateProfessionGroupPauseStatusesSuccessfully),
            map(() => showToasterMessage({ message: 'Successfully updated recruiter pause statuses.' }))
        )
    );

    showToasterPauseStatusUpdateFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateProfessionGroupPauseStatusesFailure),
            map(() => showToasterMessage({ message: 'Failed to update recruiter pause statuses.' }))
        )
    );

    showToasterProfessionGroupFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createProfessionGroupFailure, updateProfessionGroupFailure),
            map((action) => showToasterMessage({ message: action.error as string }))
        )
    );

    showToasterSetRecruiterLineOfBusinessSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterLineOfBusinessSuccessfully),
            map(() => showToasterMessage({ message: 'Successfully set recruiter line of business.' }))
        )
    );

    showToasterSetRecruiterLineOfBusinessFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(setRecruiterLineOfBusinessFailure),
            map(() => showToasterMessage({ message: 'Failed to set recruiter line of business.' }))
        )
    );
}
